import { QuestionCircleOutlined } from '@ant-design/icons'
import { Col, DatePicker, Form, Input, Row, Select, Tooltip } from 'antd'
import { useAtom } from 'jotai'
import { toNumber } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor, UsersSearch } from '@/components'
import { PriorityPopoverDescription } from '@/components/priority-popover.component'
import { userInfoAtom } from '@/modules/user'
import { PriorityEnum } from '@/schemas'
import { removeHtmlTag } from '@/utils/remove-html-tag'

import { Production, ProductionClassification, ProductionFlowStatus, ProductionType } from '../production.schema'
import { ProductionFormInputMaxCount } from './util'

const { RangePicker } = DatePicker

export const ProductionBaseInfoForm: React.FC<{ production?: Production }> = ({ production }) => {
  const [viewer] = useAtom(userInfoAtom)

  const { t } = useTranslation()

  const allowUpdateNameFlowStatus = [
    ProductionFlowStatus.待立项申请,
    ProductionFlowStatus.待指派TO,
    ProductionFlowStatus.待资源预估,
    ProductionFlowStatus.待立项审批,
    ProductionFlowStatus.待IPD审批,
  ]
  const canUpdateName =
    !production ||
    viewer.isMiniIPDCore ||
    viewer.isSuperAdmin ||
    allowUpdateNameFlowStatus.includes(production.flowStatus)
  return (
    <>
      <div className='text-base font-bold mb-4'>{t('production.detail.projectBaseInfo')}</div>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            tooltip={t('production.form.name.tooltip')}
            label={t('production.name')}
            required
            name='name'
            rules={[{ required: true }]}
          >
            <Input disabled={!canUpdateName} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('production.owner')} required name='email' rules={[{ required: true }]}>
            <UsersSearch allowClear={false} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={
              <>
                <span>{t('production.timePeriod')}</span>
                <Tooltip title={t('production.timePeriodDescription')}>
                  <QuestionCircleOutlined className='ml-4px text-gray-500' />
                </Tooltip>
              </>
            }
            required
            name='range'
            rules={[
              {
                required: true,
                validator: async (_, value) => {
                  return !value ? Promise.reject(`${t('production.timePeriod')}是必选填项!`) : Promise.resolve()
                },
              },
            ]}
          >
            <RangePicker className='w-full' format='YYYY-MM-DD' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t('production.detail.projectType')}
            required
            name='projectType'
            rules={[{ required: true }]}
          >
            {/* t('新产品'), t('现有产品') */}
            <Select
              options={Object.keys(ProductionType)
                .filter((v) => isNaN(toNumber(v)))
                .map((key) => {
                  const value = ProductionType[key]
                  return {
                    value,
                    label: t(`production.form.productionType.${key}`),
                  }
                })}
            ></Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t('production.detail.expectDeliveryTime')}
            name='expectDeliveryTime'
            rules={[{ required: true }]}
          >
            <DatePicker className='w-full' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t('production.detail.classification')}
            required
            name='classification'
            rules={[{ required: true }]}
          >
            {/* t('教育类'), t('IOT类'), t('社交泛娱乐类'), t('平台类'), t('其他') */}
            <Select
              placeholder={t('production.detail.classification')}
              options={Object.keys(ProductionClassification)
                .filter((v) => isNaN(toNumber(v)))
                .map((key) => {
                  const value = ProductionClassification[key]
                  return {
                    value,
                    label: t(`production.form.classification.${key}`),
                  }
                })}
            ></Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            required
            name='priority'
            label={
              <>
                <span>{t('production.detail.priority')}</span>
                <PriorityPopoverDescription />
              </>
            }
            rules={[{ required: true }]}
          >
            <Select
              placeholder={t('production.detail.priority')}
              options={Object.keys(PriorityEnum)
                .filter((v) => isNaN(toNumber(v)))
                .map((key) => {
                  const value = PriorityEnum[key]
                  return {
                    value,
                    label: t(`form.priority.${key}`),
                  }
                })}
            ></Select>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            valuePropName='content'
            label={
              <>
                <span>{t('production.detail.standard')}</span>
                <Tooltip title={t('production.detail.standardTimeDescription')}>
                  <QuestionCircleOutlined className='ml-4px text-gray-500' />
                </Tooltip>
              </>
            }
            name='standard'
            trigger='onChange'
            rules={[
              {
                required: true,
                validator: async (_, value) =>
                  !removeHtmlTag(value).trim()
                    ? Promise.reject(`${t('production.detail.standard')}是必选填项!`)
                    : Promise.resolve(),
              },
            ]}
          >
            <Editor
              textMaxCount={ProductionFormInputMaxCount}
              className={'editorBorder'}
              placeholder={t('production.detail.standardTimeDescription')}
              disableAutoSaveStyle
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  )
}
